// ── gameStateTape — the Live Feed tape's game-state rows ──────────
//
// Feeds the "Odds and Game State" column from both game-state lanes: the
// legacy /api/analytics/game-states poll (gameStatesStore) and the Core
// game-state lane (coreGameStates).
//
// Kept out of marketStore on purpose: a game-state change must never publish
// the market store — a score reaches its board row through the game-state
// subscription alone (GameRows.renderContract) — so the tape keeps its own log
// and only FeedTape subscribes to it.
//
// A game is logged when what the tape shows of it changes (score, status,
// period, clock, provider). Games a lane starts reporting are counted into one
// summary row instead, so a page mounting thirty games doesn't flush the log
// with thirty rows that carry no news. Seen-signatures accumulate across
// responses: several pollers write gameStatesStore with different key sets,
// and a key flapping in and out of the map is not a new game.

import { create } from 'zustand';
import type { FeedLogEntry } from './marketStore';
import { useGameStatesStore } from './gameStatesStore';
import { useCoreGameStatesStore } from '@/services/coreGameStates';
import {
  coreTapeStates,
  gameStateTapeSignature,
  pollTapeStates,
  type GameStateTapeLane,
  type GameStateTapeState,
} from '@/lib/gameState/feedTape';

const TAPE_MAX = 100;
const SEEN_MAX = 4096;

/** Newest first, capped at TAPE_MAX. Replaced (never mutated) on change. */
export const useGameStateTape = create<{ entries: readonly FeedLogEntry[] }>(() => ({ entries: [] }));

const seenByLane: Record<GameStateTapeLane, Map<string, string>> = { poll: new Map(), core: new Map() };

export function logGameStates(lane: GameStateTapeLane, states: readonly GameStateTapeState[]): void {
  const seen = seenByLane[lane];
  if (seen.size > SEEN_MAX) seen.clear();
  const ts = Date.now();
  const rows: FeedLogEntry[] = [];
  let newGames = 0;
  for (const s of states) {
    const signature = gameStateTapeSignature(s);
    const prior = seen.get(s.key);
    seen.set(s.key, signature);
    if (prior === undefined) { newGames++; continue; }
    if (prior === signature) continue;
    rows.push({
      ts,
      ticker: s.key,
      source: 'gamestate',
      sport: s.sport,
      type: 'gameState',
      gsLane: lane,
      gsStatusId: s.statusId,
      awayScore: s.awayScore,
      homeScore: s.homeScore,
      gsPeriodLabel: s.periodLabel,
      gsClock: s.gameClock,
      gsProvider: s.provider,
      gsMatchup: s.matchup,
    });
  }
  if (newGames > 0) {
    rows.push({ ts, ticker: 'new-games', source: 'gamestate', sport: '', type: 'gameState', gsLane: lane, gsNewGames: newGames });
  }
  if (rows.length === 0) return;
  const prev = useGameStateTape.getState().entries;
  useGameStateTape.setState({ entries: [...rows.reverse(), ...prev].slice(0, TAPE_MAX) });
}

const unsubscribers = [
  useGameStatesStore.subscribe((s, prev) => {
    if (s.byGameKey !== prev.byGameKey) logGameStates('poll', pollTapeStates(s.byGameKey));
  }),
  useCoreGameStatesStore.subscribe((s, prev) => {
    if (s.records !== prev.records) logGameStates('core', coreTapeStates(s.records));
  }),
];
if (import.meta.hot) import.meta.hot.dispose(() => { for (const unsub of unsubscribers) unsub(); });
